import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import {
  tmsApi,
  type BookingWorklistItem,
  type CreateOrderBookingRequest,
  type OrderBooking,
  type UpdateOrderBookingRequest,
} from '../client';
import { orderTaskKeys } from './useOrderTasks';

/**
 * Stable query-key factory for booking caches. Bookings hang off an
 * order, so the list cache is keyed by order id.
 *
 * <p>
 * Create / delete mutations invalidate the per-order task list too,
 * because the BE creates the booking AND its two paired
 * BookingDeparture + BookingArrival tasks atomically — the FE Tasks
 * panel must pick up those rows in the same render.
 * </p>
 */
export const orderBookingKeys = {
  all: ['tms', 'orderBooking'] as const,
  byOrder: (orderId: string) => ['tms', 'orderBooking', 'order', orderId] as const,
  byId: (id: string) => ['tms', 'orderBooking', 'id', id] as const,
  worklist: (organisationId: string, fromDate?: string, toDate?: string) =>
    ['tms', 'orderBooking', 'worklist', organisationId, fromDate ?? null, toDate ?? null] as const,
};

/**
 * Fetch every booking for an order, with the paired departure/arrival
 * tasks embedded as compact summaries. Pass `null` to keep the query
 * disabled while the order id is still resolving.
 */
export function useOrderBookings(
  orderId: string | null,
  options?: Omit<UseQueryOptions<OrderBooking[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<OrderBooking[], Error>({
    queryKey: orderId ? orderBookingKeys.byOrder(orderId) : orderBookingKeys.all,
    queryFn: () => {
      if (!orderId) throw new Error('No order id');
      return tmsApi.getOrderBookings(orderId);
    },
    enabled: !!orderId,
    staleTime: 30 * 1000,
    ...options,
  });
}

/** Fetch a single booking by id, with embedded task summaries. */
export function useOrderBooking(
  id: string | null,
  options?: Omit<UseQueryOptions<OrderBooking, Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<OrderBooking, Error>({
    queryKey: id ? orderBookingKeys.byId(id) : orderBookingKeys.all,
    queryFn: () => {
      if (!id) throw new Error('No booking id');
      return tmsApi.getOrderBooking(id);
    },
    enabled: !!id,
    staleTime: 30 * 1000,
    ...options,
  });
}

/**
 * POST a new booking. Creates the booking AND its two paired tasks
 * atomically on the BE — both caches must be invalidated so the
 * Bookings panel AND the Tasks panel pick up the new rows.
 */
export function useCreateOrderBooking() {
  const qc = useQueryClient();
  return useMutation<OrderBooking, Error, CreateOrderBookingRequest>({
    mutationKey: [...orderBookingKeys.all, 'create'],
    mutationFn: (body) => tmsApi.createOrderBooking(body),
    onSuccess: (_booking, vars) => {
      qc.invalidateQueries({ queryKey: orderBookingKeys.byOrder(vars.orderId) });
      qc.invalidateQueries({ queryKey: orderTaskKeys.byOrder(vars.orderId) });
    },
  });
}

/**
 * PUT a booking. Booking-level fields only — per-task edits (port times,
 * addresses) go through `useUpdateOrderTask`. Caller passes `orderId`
 * alongside the body so we can invalidate the per-order list cache.
 */
export function useUpdateOrderBooking() {
  const qc = useQueryClient();
  return useMutation<
    OrderBooking,
    Error,
    { orderId: string; body: UpdateOrderBookingRequest }
  >({
    mutationKey: [...orderBookingKeys.all, 'update'],
    mutationFn: ({ body }) => tmsApi.updateOrderBooking(body),
    onSuccess: (booking, { orderId }) => {
      qc.invalidateQueries({ queryKey: orderBookingKeys.byOrder(orderId) });
      qc.setQueryData(orderBookingKeys.byId(booking.id), booking);
    },
  });
}

/**
 * DELETE a booking by id. Removes the booking AND its two paired tasks
 * atomically on the BE — both caches must be invalidated so the
 * Bookings panel AND the Tasks panel drop the rows in the same render.
 */
export function useDeleteOrderBooking() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { orderId: string; bookingId: string }>({
    mutationKey: [...orderBookingKeys.all, 'delete'],
    mutationFn: ({ bookingId }) => tmsApi.deleteOrderBooking(bookingId),
    onSuccess: (_res, { orderId, bookingId }) => {
      qc.invalidateQueries({ queryKey: orderBookingKeys.byOrder(orderId) });
      qc.invalidateQueries({ queryKey: orderTaskKeys.byOrder(orderId) });
      qc.removeQueries({ queryKey: orderBookingKeys.byId(bookingId) });
    },
  });
}

/**
 * Fetch the Bookings cockpit worklist for an organisation over a planning
 * horizon (defaults to today .. +14 days when the range is omitted). Pass a
 * `null` organisation id to keep the query disabled while it resolves.
 *
 * <p>
 * The rows are order + booking facts only — classifying each one as a ferry
 * crossing (and into the To-Book / Booked queues) is done client-side from the
 * resolved origin/destination address countries, since the TMS doesn't carry
 * the address geography.
 * </p>
 */
export function useBookingWorklist(
  organisationId: string | null,
  range?: { fromDate?: string; toDate?: string },
  options?: Omit<UseQueryOptions<BookingWorklistItem[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<BookingWorklistItem[], Error>({
    queryKey: organisationId
      ? orderBookingKeys.worklist(organisationId, range?.fromDate, range?.toDate)
      : orderBookingKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return tmsApi.getBookingWorklist(organisationId, range?.fromDate, range?.toDate);
    },
    enabled: !!organisationId,
    staleTime: 30 * 1000,
    ...options,
  });
}
